// File-based document I/O.
//
// The engine deliberately provides none of this — see the note at
// engineStore.ts:565 in the engine repo: "File storage is a host-app concern."
// It exposes saveDocument/loadDocument and emits `sketch-save-requested` on
// Ctrl+S, and the host decides what a "file" means. Here that means a local
// download/upload; cloud persistence is a separate, deferred feature.
//
// Without this composable a user has no way to keep their work.

import { ref } from 'vue'

import { DEFAULT_DOCUMENT_NAME, DOCUMENT_EXTENSION } from '../branding'
import type { ExportImageOptions, SketchElement } from '../types/sketcher'
import { confirmDialog } from '../ui/dialogs'

/** Strips characters that are illegal or awkward in filenames across platforms. */
function toFilename(name: string, extension: string): string {
  const cleaned = name.replace(/[^\w\-. ]+/g, '').trim() || DEFAULT_DOCUMENT_NAME
  return `${cleaned}${extension}`
}

export function useDocumentIO(getElement: () => SketchElement | null) {
  const documentName = ref(DEFAULT_DOCUMENT_NAME)
  const busy         = ref(false)
  const error        = ref<string | null>(null)

  /** True when there are unsaved changes. Cheap enough to poll from the UI. */
  function isDirty(): boolean {
    try {
      return getElement()?.isDirty() ?? false
    } catch {
      return false
    }
  }

  async function confirmDiscard(): Promise<boolean> {
    if (!isDirty()) return true
    return confirmDialog({
      title:        'Unsaved changes',
      message:      'You have unsaved changes. Discard them?',
      confirmLabel: 'Discard',
      danger:       true,
    })
  }

  async function newDocument(): Promise<void> {
    const el = getElement()
    if (!el || busy.value) return
    if (!(await confirmDiscard())) return

    busy.value = true
    error.value = null
    try {
      documentName.value = DEFAULT_DOCUMENT_NAME
      await el.newDocument({ name: documentName.value })
    } catch {
      error.value = 'Could not create a new drawing.'
    } finally {
      busy.value = false
    }
  }

  async function openDocument(): Promise<void> {
    const el = getElement()
    if (!el || busy.value) return
    if (!(await confirmDiscard())) return

    const input = document.createElement('input')
    input.type   = 'file'
    input.accept = `${DOCUMENT_EXTENSION},.json`

    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      busy.value = true
      error.value = null
      try {
        const text = await file.text()
        await el.loadDocument(text)
        // Drop the extension for display; the engine does not surface a name.
        documentName.value = file.name.replace(/\.[^.]+$/, '') || DEFAULT_DOCUMENT_NAME
      } catch {
        error.value = 'That file could not be opened. It may be corrupt or from a newer version.'
      } finally {
        busy.value = false
      }
    }

    input.click()
  }

  async function saveDocument(): Promise<void> {
    const el = getElement()
    if (!el || busy.value) return

    busy.value = true
    error.value = null
    let url: string | null = null
    try {
      const data = await el.saveDocument()
      const blob = new Blob([data as string], { type: 'application/json' })
      url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href     = url
      a.download = toFilename(documentName.value, DOCUMENT_EXTENSION)
      a.click()
    } catch {
      error.value = 'Could not save the drawing.'
    } finally {
      // Revoking synchronously after click() is safe: the browser has already
      // resolved the blob by then. Leaving it unrevoked leaks the whole document
      // for the lifetime of the tab.
      if (url) URL.revokeObjectURL(url)
      busy.value = false
    }
  }

  async function exportImage(options: ExportImageOptions): Promise<void> {
    const el = getElement()
    if (!el || busy.value) return

    busy.value = true
    error.value = null
    let url: string | null = null
    try {
      const blob = await el.exportImage(options)
      url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href     = url
      a.download = toFilename(documentName.value, `.${options.format}`)
      a.click()
    } catch {
      error.value = 'Could not export the drawing.'
    } finally {
      if (url) URL.revokeObjectURL(url)
      busy.value = false
    }
  }

  /**
   * Wires Ctrl+S (via the engine's `sketch-save-requested` event) and the
   * unsaved-changes prompt. Returns a teardown function.
   */
  function attach(el: SketchElement): () => void {
    const onSaveRequested = () => { void saveDocument() }
    el.addEventListener('sketch-save-requested', onSaveRequested)

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return
      // Drawings live only in the tab until saved to a file, so a reload
      // silently destroys work. This is the one guard against that.
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      el.removeEventListener('sketch-save-requested', onSaveRequested)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }

  return {
    documentName,
    busy,
    error,
    isDirty,
    newDocument,
    openDocument,
    saveDocument,
    exportImage,
    attach,
  }
}
